import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  DollarSign, Target, Calendar, TrendingUp, Trophy, Clock,
  ChevronRight, Plus, Users, Gift, Heart, Shield
} from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FundraisingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [householdProgress, setHouseholdProgress] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [householdId, setHouseholdId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const campaignsRes = await axios.get(`${API_URL}/api/fundraising/campaigns?status=active`, {
        withCredentials: true
      });
      setCampaigns(campaignsRes.data);

      // Get household ID for parents
      if (user?.role === 'parent') {
        const dashRes = await axios.get(`${API_URL}/api/dashboard/parent`, { withCredentials: true });
        if (dashRes.data.household_id) {
          setHouseholdId(dashRes.data.household_id);
          const progressRes = await axios.get(
            `${API_URL}/api/fundraising/household/${dashRes.data.household_id}`,
            { withCredentials: true }
          );
          setHouseholdProgress(progressRes.data);
        }
      }

      // Get stats for admin
      if (user?.role === 'league_admin' || user?.role === 'super_admin') {
        const statsRes = await axios.get(`${API_URL}/api/fundraising/stats`, { withCredentials: true });
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCampaignTypeIcon = (type) => {
    const icons = {
      popcorn_sales: '🍿',
      cookie_sales: '🍪',
      bingo_night: '🎱',
      shake_helmet: '🪖',
      restaurant_night: '🍕',
      merchandise: '👕',
      donation_drive: '💝',
      sponsor: '🤝',
      other: '💰'
    };
    return icons[type] || icons.other;
  };

  const getCampaignTypeColor = (type) => {
    const colors = {
      popcorn_sales: 'bg-yellow-100 text-yellow-800',
      cookie_sales: 'bg-pink-100 text-pink-800',
      bingo_night: 'bg-purple-100 text-purple-800',
      shake_helmet: 'bg-navy/10 text-navy',
      restaurant_night: 'bg-orange-100 text-orange-800',
      merchandise: 'bg-blue-100 text-blue-800',
      donation_drive: 'bg-red/10 text-red',
      sponsor: 'bg-green-100 text-green-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors.other;
  };

  const isAdmin = user?.role === 'league_admin' || user?.role === 'super_admin';
  
  // Only President and Treasurer can create campaigns
  const canCreateCampaign = isAdmin && (
    user?.title?.toLowerCase().includes('president') || 
    user?.title?.toLowerCase().includes('treasurer') ||
    user?.role === 'super_admin'
  );

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded-lg w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="fundraising-page">
        {/* Header - Athletic Style */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Support the Team</p>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-navy uppercase tracking-tighter mb-1">Fundraising</h1>
            <p className="text-gray-600 font-body">Support the Park City Chiefs</p>
          </div>
          {canCreateCampaign && (
            <Button 
              className="mt-4 sm:mt-0 bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider"
              onClick={() => navigate('/admin/fundraising/new')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          )}
        </div>

        {/* Admin Stats - Athletic Cards */}
        {isAdmin && stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <p className="font-display text-2xl font-black text-green-600">
                ${stats.total_raised.toLocaleString()}
              </p>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Total Raised</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
              <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center mb-3">
                <Target className="w-5 h-5 text-navy" />
              </div>
              <p className="font-display text-2xl font-black text-navy">{stats.active_campaigns}</p>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Active</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
              <div className="w-10 h-10 bg-light-blue/10 rounded-lg flex items-center justify-center mb-3">
                <Users className="w-5 h-5 text-light-blue" />
              </div>
              <p className="font-display text-2xl font-black text-light-blue">{stats.participation_count}</p>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Participants</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
              <div className="w-10 h-10 bg-red/10 rounded-lg flex items-center justify-center mb-3">
                <Clock className="w-5 h-5 text-red" />
              </div>
              <p className="font-display text-2xl font-black text-red">{stats.campaigns_ending_soon}</p>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Ending Soon</p>
            </div>
          </div>
        )}

        {/* Zeffy Donations Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6" data-testid="donations-section">
          <div className="px-5 py-4 border-b border-gray-100 relative overflow-hidden">
            {/* Field Background */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('/field-bg-3.jpg')` }}
            />
            {/* Light Navy Gradient Overlay (right to left, 5% to 1%) */}
            <div className="absolute inset-0 bg-gradient-to-l from-navy/[0.01] via-navy/[0.03] to-navy/[0.05]" />
            {/* Base navy overlay for text readability */}
            <div className="absolute inset-0 bg-navy/70" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-black text-white uppercase tracking-tight">Make a Donation</h2>
                  <p className="text-white/70 text-sm font-body">Support Park City Chiefs directly</p>
                </div>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 bg-gold text-navy rounded-full">100% Goes to Kids</span>
            </div>
          </div>
          <div className="p-5">
            <p className="text-gray-600 mb-4 font-body">
              Your generous donations help fund equipment, field maintenance, scholarships for families in need, and special events for our athletes. Every dollar makes a difference!
            </p>
            
            {/* Zeffy Widget Embed Container */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4">
              {/* Zeffy Thermometer Widget */}
              <div 
                className="relative overflow-hidden w-full"
                style={{ paddingTop: '120px' }}
              >
                <iframe 
                  title="Donation form powered by Zeffy"
                  style={{
                    position: 'absolute',
                    border: 0,
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    width: '100%',
                    height: '120px'
                  }}
                  src="https://www.zeffy.com/embed/thermometer/donate-to-make-a-difference-16090"
                  allowtransparency="true"
                />
              </div>
              
              {/* Donate Button */}
              <div className="text-center mt-4">
                <a
                  href="https://www.zeffy.com/donation-form/donate-to-make-a-difference-16090"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-3 bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider text-sm rounded-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                  data-testid="donate-now-btn"
                >
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </a>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500">
              <span className="flex items-center"><Shield className="w-4 h-4 mr-1 text-green-600" /> Secure Payment</span>
              <span className="flex items-center"><DollarSign className="w-4 h-4 mr-1 text-green-600" /> Tax Deductible</span>
              <span className="flex items-center"><Heart className="w-4 h-4 mr-1 text-red" /> 0% Platform Fees</span>
            </div>
          </div>
        </div>

        {/* Household Progress (Parents) */}
        {user?.role === 'parent' && householdProgress && householdProgress.campaigns?.length > 0 && (
          <Card className="mb-6 border-gold">
            <CardHeader>
              <CardTitle className="text-navy flex items-center">
                <Trophy className="w-5 h-5 mr-2 text-gold" />
                Your Family's Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {householdProgress.campaigns.slice(0, 4).map(progress => (
                  <div 
                    key={progress.campaign_id}
                    className="p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-navy">{progress.campaign_title}</span>
                      <Badge className={getCampaignTypeColor(progress.campaign_type)}>
                        {getCampaignTypeIcon(progress.campaign_type)}
                      </Badge>
                    </div>
                    <div className="mb-2">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">
                          ${progress.amount_raised.toFixed(2)} raised
                        </span>
                        <span className="font-medium text-navy">
                          {progress.progress_percent.toFixed(0)}%
                        </span>
                      </div>
                      <Progress value={progress.progress_percent} className="h-2" />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Goal: ${progress.goal_amount.toLocaleString()}</span>
                      <span>{progress.days_remaining} days left</span>
                    </div>
                    {progress.status === 'goal_met' && (
                      <Badge className="mt-2 bg-green-100 text-green-800">
                        <Trophy className="w-3 h-3 mr-1" /> Goal Met!
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Raised by Your Family</span>
                  <span className="text-2xl font-bold text-green-600">
                    ${householdProgress.total_raised.toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Campaigns */}
        <h2 className="text-xl font-bold text-navy mb-4">Active Campaigns</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {campaigns.length > 0 ? (
            campaigns.map(campaign => (
              <Card 
                key={campaign.campaign_id}
                className="cursor-pointer hover:border-navy/30 transition-colors overflow-hidden"
                onClick={() => navigate(`/fundraising/${campaign.campaign_id}`)}
                data-testid={`campaign-${campaign.campaign_id}`}
              >
                <div className={`h-2 ${(campaign.progress_percent || 0) >= 100 ? 'bg-green-500' : 'bg-navy'}`} 
                     style={{ width: `${Math.min(campaign.progress_percent || 0, 100)}%` }} />
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="text-3xl">{getCampaignTypeIcon(campaign.campaign_type)}</div>
                      <div>
                        <h3 className="font-semibold text-navy">{campaign.title}</h3>
                        {campaign.team_name && (
                          <Badge variant="outline" className="mt-1">{campaign.team_name}</Badge>
                        )}
                      </div>
                    </div>
                    <Badge className={getCampaignTypeColor(campaign.campaign_type)}>
                      {(campaign.campaign_type || 'other').replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {campaign.description}
                  </p>

                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">
                          ${(campaign.current_amount || 0).toLocaleString()} raised
                        </span>
                        <span className="font-bold text-navy">
                          {(campaign.progress_percent || 0).toFixed(0)}%
                        </span>
                      </div>
                      <Progress value={campaign.progress_percent || 0} className="h-3" />
                    </div>

                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center text-gray-500">
                        <Target className="w-4 h-4 mr-1" />
                        Goal: ${(campaign.goal_amount || 0).toLocaleString()}
                      </div>
                      <div className={`flex items-center ${campaign.days_remaining <= 3 ? 'text-red' : 'text-gray-500'}`}>
                        <Clock className="w-4 h-4 mr-1" />
                        {campaign.days_remaining} days left
                      </div>
                    </div>

                    {campaign.participation_count > 0 && (
                      <div className="flex items-center text-sm text-gray-500">
                        <Users className="w-4 h-4 mr-1" />
                        {campaign.participation_count} families participating
                      </div>
                    )}
                  </div>

                  {(campaign.progress_percent || 0) >= 100 && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg text-center">
                      <Trophy className="w-6 h-6 text-green-600 mx-auto mb-1" />
                      <p className="text-green-700 font-medium">Goal Reached!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-2 text-center py-12">
              <Gift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No active campaigns</p>
              {isAdmin && (
                <Button 
                  className="mt-4 bg-navy hover:bg-navy/90"
                  onClick={() => navigate('/admin/fundraising/new')}
                >
                  Create First Campaign
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Recent Contributions (Admin Only) */}
        {isAdmin && householdProgress?.contributions?.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="text-navy">Recent Contributions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {householdProgress.contributions.slice(0, 5).map(contribution => (
                  <div 
                    key={contribution.contribution_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-navy">
                        ${contribution.amount.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {contribution.notes || contribution.contribution_type}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {format(parseISO(contribution.created_at), 'MMM d')}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ParentLayout>
  );
};

export default FundraisingPage;
